<template>
    <div v-if="node">
        <div class="rounded-md space-y-2">
            <Accordion class="pl-2 pr-2 space-y-1 bg-opacity-30 bg-slate-400"
                :title='node.obj?.toUpperCase() + " Properties"'
                :modified="objectPropertiesModified">
                <div class="grid grid-cols-2 gap-x-2 gap-y-1 pt-1 pb-2 border-b border-slate-400" v-if="node.obj !== 'page' && node.obj !== 'template_page' && !node.no_geom">
                    <div class="flex items-center space-x-1">
                        <span class="text-xs text-gray-300 w-4 shrink-0">x</span>
                        <input type="number" class="w-full px-1 text-gray-900 appearance-none rounded border border-gray-400 hover:border-gray-500 focus:border-blue-500 focus:outline-none text-sm"
                            v-model.number="geom_x" @focus="syncGeom" @change="applyGeom('x')" @keydown.enter.prevent="applyGeom('x')" />
                    </div>
                    <div class="flex items-center space-x-1">
                        <span class="text-xs text-gray-300 w-4 shrink-0">y</span>
                        <input type="number" class="w-full px-1 text-gray-900 appearance-none rounded border border-gray-400 hover:border-gray-500 focus:border-blue-500 focus:outline-none text-sm"
                            v-model.number="geom_y" @focus="syncGeom" @change="applyGeom('y')" @keydown.enter.prevent="applyGeom('y')" />
                    </div>
                    <template v-if="node.obj === 'qrcode'">
                        <div class="col-span-2 flex items-center space-x-1">
                            <span class="text-xs text-gray-300 w-8 shrink-0">size</span>
                            <input type="number" class="w-full px-1 text-gray-900 appearance-none rounded border border-gray-400 hover:border-gray-500 focus:border-blue-500 focus:outline-none text-sm"
                                v-model.number="geom_w" @focus="syncGeom" @change="applyGeom('size')" @keydown.enter.prevent="applyGeom('size')" />
                        </div>
                    </template>
                    <template v-else-if="node.hasProperty('width')">
                        <div class="flex items-center space-x-1">
                            <span class="text-xs text-gray-300 w-4 shrink-0">w</span>
                            <input type="number" :disabled="node.size_kind === 'auto' || isTrackImageSizeLocked()" :title="geometrySizeTitle()"
                                class="w-full px-1 text-gray-900 appearance-none rounded border border-gray-400 hover:border-gray-500 focus:border-blue-500 focus:outline-none text-sm disabled:bg-gray-300 disabled:text-gray-500"
                                v-model.number="geom_w" @focus="syncGeom" @change="applyGeom('w')" @keydown.enter.prevent="applyGeom('w')" />
                        </div>
                        <div class="flex items-center space-x-1">
                            <span class="text-xs text-gray-300 w-4 shrink-0">h</span>
                            <input type="number" :disabled="node.size_kind === 'auto' || isTrackImageSizeLocked()" :title="geometrySizeTitle()"
                                class="w-full px-1 text-gray-900 appearance-none rounded border border-gray-400 hover:border-gray-500 focus:border-blue-500 focus:outline-none text-sm disabled:bg-gray-300 disabled:text-gray-500"
                                v-model.number="geom_h" @focus="syncGeom" @change="applyGeom('h')" @keydown.enter.prevent="applyGeom('h')" />
                        </div>
                    </template>
                </div>
                <div v-if="node.haspid !== undefined && !node.hasProperty('no-haspid')" class="flex items-center gap-1">
                    <form-input class="flex-1 min-w-0" name="Hasp id" type="number" :no_scroll=true :model-value="node.haspid"
                        v-bind="objectStatus('haspid')"
                        @update:modelValue="setHaspId" />
                    <span v-if="runtimeIdError" class="text-[10px] text-red-300" :title="runtimeIdError">!</span>
                    <button v-if="runtimeIdError && suggestedHaspId !== null" type="button"
                        class="self-center shrink-0 px-1 h-6 text-[10px] text-slate-300 hover:text-white hover:bg-slate-600 rounded"
                        title="Use the next free Designer object ID"
                        @click.stop="setHaspId(suggestedHaspId)">
                        Use {{ suggestedHaspId }}
                    </button>
                    <button v-if="conversionTargets.length > 0" type="button"
                        class="self-center shrink-0 w-6 h-6 text-slate-300 hover:text-white hover:bg-slate-600 rounded"
                        title="Convert object type" aria-label="Convert object type"
                        @click.stop="openConversionDialog">
                        <font-awesome-icon icon="fa-solid fa-edit" class="text-[11px]" />
                    </button>
                </div>
                <p v-if="runtimeIdError" class="text-[10px] text-red-300">{{ runtimeIdError }}</p>
                <form-input name="comment" type="text" :no_scroll=true v-model="node.comment"
                    v-bind="objectStatus('comment')"
                    v-if="!node.hasProperty('no_comment')"
                    v-on:input="node.getLayer().layerCallback('node-comment', node)" />
                <form-checkbox label="swipe" v-bind="objectStatus('swipe', 'set-swipe')"
                    v-if="node.hasProperty('swipe') && node.obj !== 'template_page'" v-model="node.swipe"
                    v-on:change="node.adjust('set-swipe', $event)" />

                <!-- Template page: target page (underlay + runtime page) -->
                <!-- Designer-only link to the page this template underlays: not a
                     firmware property and it has no default to fall back to, so the
                     status column stays reserved but empty. -->
                <property-row v-if="node.obj === 'template_page'" label="target page" :resettable="false">
                    <select
                        class="w-full min-w-0 px-1 text-gray-900 rounded border border-gray-400 hover:border-gray-500 focus:border-blue-500 focus:outline-none text-sm"
                        :value="node.targetPage"
                        @change="setTargetPage($event.target.value)">
                        <option :value="null">— none —</option>
                        <option v-for="p in regularPages" :key="p.haspid" :value="p.haspid">
                            P{{ p.haspid }}{{ p.comment ? ' – ' + p.comment : '' }}
                        </option>
                    </select>
                </property-row>

                <div class="space-y-1" v-if="node.obj !== 'template_page'">

                    <!-- Size Mode: Auto derives w/h from content (see HaspLegend's
                         computeContentSize) — Width/Height above are disabled and
                         resize handles are hidden (updateTransformer) while active. -->
                    <div class="flex flex-col space-y-1 pt-1 pb-1 border-b border-t border-slate-400" v-if="node.hasProperty('size_kind')">
                        <form-label>Size Mode</form-label>
                        <div class="flex space-x-2 text-white">
                            <div class="flex-1 flex flex-col items-center text-center">
                                <input type="radio" value="fixed" v-model="node.size_kind" v-on:change="onSizeModeChange" />
                                <span class="mt-1 text-xs w-full">Fixed</span>
                            </div>
                            <div class="flex-1 flex flex-col items-center text-center">
                                <input type="radio" value="auto" v-model="node.size_kind" v-on:change="onSizeModeChange" />
                                <span class="mt-1 text-xs w-full">Auto</span>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col space-y-1 pt-1 pb-1 border-b border-t border-slate-400" v-if="stretchModeOptions().length > 0">
                        <form-label>Stretch Mode</form-label>
                        <div class="flex space-x-2 text-white">
                            <div v-for="mode in stretchModeOptions()" :key="mode"
                                class="flex-1 flex flex-col items-center text-center">
                                <input type="radio" name="stretch-mode" :value="mode"
                                    :checked="node.stretch_mode === mode"
                                    v-on:change="setStretchMode(mode)" />
                                <span class="mt-1 text-xs w-full">{{ stretchModeLabel(mode) }}</span>
                            </div>
                        </div>
                    </div>

                <form-checkbox label="touch" v-model="node.enabled" v-bind="objectStatus('enabled', 'set-enabled')" v-if="!node.no_touch"
                    v-on:change="node.adjust('set-enabled', $event)" />
                <form-checkbox label="hidden" v-model="node.hidden" v-bind="objectStatus('hidden', 'set-hidden')" v-if="!node.no_hidde"
                    v-on:change="node.adjust('set-hidden', $event)" />
                <property-row v-if="node.obj === 'badge'" label="Text"
                    v-bind="objectStatus('text', 'text_text')">
                    <div class="relative">
                        <input type="text"
                            class="w-full pr-7 px-1 text-gray-900 appearance-none placeholder-gray-500 rounded border border-gray-400 hover:border-gray-500 focus:border-blue-500 focus:outline-none"
                            v-model="displayText"
                            @input="node.adjust('text_text')"
                        />
                        <button
                            @click="showIconPicker = true"
                            class="absolute right-0 top-0 h-full px-1 bg-blue-600 text-white rounded-r hover:bg-blue-500 transition-colors"
                            title="Insert OpenHASP Icon">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-.464 5.535a1 1 0 10-1.415-1.414 3 3 0 01-4.242 0 1 1 0 00-1.415 1.414 5 5 0 007.072 0z" clip-rule="evenodd" />
                            </svg>
                        </button>
                    </div>
                </property-row>
                <form-input v-if="node.obj === 'badge'" name="text font" type="number" :no_scroll=true min="8"
                    :model-value="node.styleProp('main', 'text_font')" v-bind="mainStyleStatus('text_font')"
                    @update:model-value="node.setStyle('main', 'text_font', Number($event))" />
                <property-row v-if="node.obj === 'badge'" label="Text Font Family"
                    v-bind="objectStatus('text_font_resource', 'text_font_resource')">
                    <component :is="resourceOverride('font')" resource-type="font" :model-value="node.text_font_resource"
                        @update:model-value="onResourcePropertyChange('text_font_resource', $event)" />
                </property-row>
                <property-row v-if="node.obj === 'badge'" label="Value string"
                    v-bind="objectStatus('value_str', 'value_str')">
                    <div class="relative">
                        <input type="text"
                            class="w-full pr-7 px-1 text-gray-900 appearance-none placeholder-gray-500 rounded border border-gray-400 hover:border-gray-500 focus:border-blue-500 focus:outline-none"
                            v-model="displayValueStr"
                            @input="node.adjust('value_str')"
                        />
                        <button
                            @click="showValueStrPicker = true"
                            class="absolute right-0 top-0 h-full px-1 bg-blue-600 text-white rounded-r hover:bg-blue-500 transition-colors"
                            title="Insert OpenHASP Icon">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-.464 5.535a1 1 0 10-1.415-1.414 3 3 0 01-4.242 0 1 1 0 00-1.415 1.414 5 5 0 007.072 0z" clip-rule="evenodd" />
                            </svg>
                        </button>
                    </div>
                </property-row>
                <form-input v-if="node.obj === 'badge'" name="value font" type="number" :no_scroll=true min="8"
                    v-model="node.value_font" v-bind="objectStatus('value_font', 'value_font')"
                    v-on:input="node.adjust('value_font')" />
                <property-row v-if="node.obj === 'badge'" label="Value Font Family"
                    v-bind="objectStatus('value_font_resource', 'value_font_resource')">
                    <component :is="resourceOverride('font')" resource-type="font" :model-value="node.value_font_resource"
                        @update:model-value="onResourcePropertyChange('value_font_resource', $event)" />
                </property-row>
                <property-row v-if="node.obj === 'connection'" label="Line color"
                    v-bind="objectStatus('line_color', 'line_color')">
                    <ColorPicker :key="editorKey('connection:line_color')" v-model:pureColor="node.line_color" useType="pure" format="hex8" lang="En" shape="circle"
                        v-on:pureColorChange="node.adjust('line_color')" />
                </property-row>
                <form-input v-if="node.obj === 'connection'" name="line width" type="number" min="1"
                    v-model.number="node.line_width" v-bind="objectStatus('line_width', 'line_width')"
                    v-on:input="node.adjust('line_width')" />
                <form-input v-if="node.obj === 'connection'" name="corner radius" type="number" min="0"
                    v-model.number="node.corner_radius" v-bind="objectStatus('corner_radius', 'corner_radius')"
                    v-on:input="node.adjust('corner_radius')" />

                <form-input name="angle" type="number" :no_scroll=true min="0" max="360" v-model="node.hasp_angle"
                    v-bind="objectStatus('angle', 'angle')"
                    v-if="node.hasProperty('angle')" v-on:input="node.adjust('angle')" />
                    <form-input name="rotation" type="number" :no_scroll=true min="0" max="360"
                        v-model="node.hasp_rotation" v-bind="objectStatus('rotation', 'rotation')"
                        v-if="node.hasProperty('rotation')"
                        v-on:input="node.adjust('rotation')" />

                    <form-input name="pivot x" type="number" :no_scroll=true v-model="node.pivot_x"
                        v-bind="objectStatus('pivot_x', 'pivot-x')"
                        v-if="node.hasProperty('pivot_x')" v-on:input="node.adjust('pivot-x')" />
                    <form-input name="pivot y" type="number" :no_scroll=true v-model="node.pivot_y"
                        v-bind="objectStatus('pivot_y', 'pivot-y')"
                        v-if="node.hasProperty('pivot_y')" v-on:input="node.adjust('pivot-y')" />
                    <form-input name="zoom" type="number" :no_scroll=true v-model="node.zoom"
                        v-bind="objectStatus('zoom', 'zoom')"
                        v-if="node.hasProperty('zoom')" v-on:input="node.adjust('zoom')" />

                    <form-input name="line count" type="number" :no_scroll=true min="0" v-model="node.line_count"
                        v-bind="objectStatus('line_count', 'line-count')"
                        v-if="node.hasProperty('line_count')" v-on:input="node.adjust('line-count')" />
                    <form-input name="label count" type="number" :no_scroll=true min="0" v-model="node.label_count"
                        v-bind="objectStatus('label_count', 'label-count')"
                        v-if="node.hasProperty('label_count')" v-on:input="node.adjust('label-count')" />
                    <form-checkbox v-if="node.obj === 'checkbox'" label="checked" v-model="node.checked"
                        v-bind="objectStatus('checked', 'checked')"
                        v-on:change="node.adjust('checked')" />

                    <!-- Designer-only: simulates the real firmware's open/closed popup
                         appearance on canvas. Never exported — see OBJECT_PROP_SKIP. -->
                    <form-checkbox label="Preview Expanded" v-if="node.hasProperty('previewExpanded')"
                        v-model="node.previewExpanded" v-on:change="node.adjust('preview-expanded')"
                        title="Toggle to simulate the dropdown's open/closed state. Double-click the object on canvas to toggle." />

                    <!-- Main part style properties — driven by v2 schema -->
                    <!-- Legend exclude list: direction/columns/spacing/show_label/show_unit move
                         to the curated "Layout" accordion below (values_order gets its own
                         control there too); marker_size/marker_shape/text_font/label_color are
                         superseded by the new marker/label/value parts (own accordions) — see
                         legend.js. -->
                    <PartPropertiesPanel v-if="node.schemaV2" :key="editorKey('main')" :node="node" partName="main"
                        :exclude="mainExclude" />

                    <form-input name="speed" type="number" min="100" step="100" v-model="node.speed"
                        v-bind="objectStatus('speed', 'speed')"
                        v-if="node.hasProperty('speed')" v-on:input="node.adjust('speed')" />
                    <form-checkbox label="direction" v-bind="objectStatus('direction', 'direction')"
                        v-if="node.hasProperty('direction') && node.obj !== 'legend'" v-model="node.direction"
                        v-on:change="node.adjust('direction', $event)" />
                    <form-checkbox label="anmimation" v-if="node.hasProperty('animation')" v-model="node.animation"
                        v-bind="objectStatus('animation', null, value => node.setAnimation(value))"
                        v-on:change="node.setAnimation($event)" />
                    <form-checkbox label="toggle" v-bind="objectStatus('toggle', 'toggle')"
                        v-if="node.hasProperty('toggle')" v-model="node.toggle"
                        v-on:change="node.adjust('toggle', $event)" />
                    <form-checkbox label="one_check" v-bind="objectStatus('one_check', 'one-check')"
                        v-if="node.hasProperty('one_check')" v-model="node.one_check"
                        v-on:change="node.adjust('one-check')" />

                    <form-input name="text font" type="number" :no_scroll=true min="8" v-model="node.text_font"
                        v-bind="objectStatus('text_font', 'text_font')"
                        v-if="node.obj === 'gauge' && node.hasProperty('text_font')" v-on:input="node.adjust('text_font')" />


                    <property-row v-if="node.hasProperty('value_str') && node.obj !== 'badge'" label="value string"
                        v-bind="objectStatus('value_str', 'value_str')">
                        <div class="relative">
                            <input type="text"
                                class="w-full pr-7 px-1 text-gray-900 appearance-none placeholder-gray-500 rounded border border-gray-400 hover:border-gray-500 focus:border-blue-500 focus:outline-none"
                                v-model="displayValueStr"
                                @input="node.adjust('value_str')"
                            />
                            <button
                                @click="showValueStrPicker = true"
                                class="absolute right-0 top-0 h-full px-1 bg-blue-600 text-white rounded-r hover:bg-blue-500 transition-colors"
                                title="Insert OpenHASP Icon">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-.464 5.535a1 1 0 10-1.415-1.414 3 3 0 01-4.242 0 1 1 0 00-1.415 1.414 5 5 0 007.072 0z" clip-rule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </property-row>
                    <form-input name="value font" type="number" :no_scroll=true min="8" v-model="node.value_font"
                        v-bind="objectStatus('value_font', 'value_font')"
                        v-if="node.hasProperty('value_font') && node.obj !== 'badge'" v-on:input="node.adjust('value_font')" />

                    <!-- <form-input name="text string" type="text" :no_scroll=true v-model="node.text"
                        v-if="node.hasProperty('text')" v-on:input="node.adjust('text_text')"
                        :completion="['%ip%', '%hostname%', '%ssid%', '%version%', '%model%']" /> -->
                    <property-row v-if="node.hasProperty('text') && node.obj !== 'badge'" label="text string"
                        v-bind="objectStatus('text', 'text_text')">
                        <div class="relative">
                            <input type="text"
                                class="w-full pr-7 px-1 text-gray-900 appearance-none placeholder-gray-500 rounded border border-gray-400 hover:border-gray-500 focus:border-blue-500 focus:outline-none"
                                v-model="displayText"
                                @input="node.adjust('text_text')"
                            />
                            <button
                                @click="showIconPicker = true"
                                class="absolute right-0 top-0 h-full px-1 bg-blue-600 text-white rounded-r hover:bg-blue-500 transition-colors"
                                title="Insert OpenHASP Icon">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-.464 5.535a1 1 0 10-1.415-1.414 3 3 0 01-4.242 0 1 1 0 00-1.415 1.414 5 5 0 007.072 0z" clip-rule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </property-row>

                    <property-row v-if="node.hasProperty('template')" label="template"
                        v-bind="objectStatus('template', 'template_text')">
                        <div class="relative">
                            <input type="text"
                                class="w-full pr-7 px-1 text-gray-900 appearance-none placeholder-gray-500 rounded border border-gray-400 hover:border-gray-500 focus:border-blue-500 focus:outline-none"
                                v-model="node.template"
                                placeholder="strftime format"
                                @input="node.adjust('template_text')"
                            />
                            <button
                                @click="showTemplatePicker = true"
                                class="absolute right-0 top-0 h-full px-1 bg-green-700 text-white rounded-r hover:bg-green-600 transition-colors"
                                title="Build strftime template">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </property-row>
                    <!-- Inline resource properties (`meta.inline: true`) — e.g. label.js's
                         `font`, gauge.js's `value_font_resource`, msgbox.js's
                         `item_font_resource`. Rendered flat here (not in their own
                         Accordion, unlike the generic resourceProperties loop below) since
                         they read as part of a widget's main properties. Schema-driven:
                         a new inline resource property of any name never requires
                         template changes here. -->
                    <property-row v-for="[propName, meta] in inlineResourceProperties" :key="propName"
                        :label="meta.label"
                        v-bind="objectStatus(propName, propName)">
                        <component
                            v-if="resourceOverride(meta.resourceType)"
                            :is="resourceOverride(meta.resourceType)"
                            :resource-type="meta.resourceType"
                            :model-value="node[propName]"
                            @update:model-value="onResourcePropertyChange(propName, $event)" />
                        <ResourcePicker
                            v-else
                            :resource-type="meta.resourceType"
                            :model-value="node[propName]"
                            @update:model-value="onResourcePropertyChange(propName, $event)" />
                    </property-row>

                    <form-select name="text decor" :model-value="node.text_decor" v-bind="mainStyleStatus('text_decor')"
                        v-if="node.hasProperty('text_decor')"
                        @update:model-value="node.setStyle('main', 'text_decor', $event)">
                        <option :value=item.value v-for="(item) in node.hasp_text_decor_options" :key=item.description>
                            {{ item.description }}</option>
                    </form-select>

                    <form-select name="align" v-model="node.align" v-bind="objectStatus('align', 'text_align')"
                        v-if="node.hasProperty('align')"
                        v-on:change="node.adjust('text_align')">
                        <option :value=item.value v-for="(item) in node.hasp_align_options" :key=item.description>
                            {{ item.description }}</option>
                    </form-select>
                    <form-select name="mode" v-model="node.mode" v-bind="objectStatus('mode', 'hasp_mode')"
                        v-if="node.hasProperty('mode')"
                        v-on:change="node.adjust('hasp_mode')">
                        <option :value=item.value v-for="(item) in node.hasp_mode_options" :key=item.description>
                            {{ item.description }}</option>
                    </form-select>
                    <form-checkbox label="Mode fixed" v-model="node.mode_fixed"
                        v-bind="objectStatus('mode_fixed', 'mode_fixed')"
                        v-if="node.hasProperty('mode_fixed')"
                        v-on:change="node.adjust('mode_fixed')" />
                    <form-select name="type" v-model="node.type" v-bind="objectStatus('type', 'type')"
                        v-if="node.hasProperty('type') && node.hasp_type_options?.length"
                        v-on:change="node.adjust('type')">
                        <option :value=item.value v-for="(item) in node.hasp_type_options" :key=item.description>
                            {{ item.description }}</option>
                    </form-select>

                    <ListOptionsTable @updateNode="node.adjust('update')" v-model:options="node.listOptions"
                        v-if="node.hasProperty('list_options')" />

                    <form-checkbox label="modal" v-bind="objectStatus('modal', 'modal')"
                        v-if="node.hasProperty('modal')" v-model="node.modal"
                        v-on:change="node.adjust('modal')" />

                    <form-input name="auto close (ms)" type="number" :no_scroll=true min="0" v-model="node.auto_close"
                        v-bind="objectStatus('auto_close', 'auto_close')"
                        v-if="node.hasProperty('auto_close')" v-on:input="node.adjust('auto_close')" />

                    <form-select name="button position" v-model="node.btn_pos" v-bind="objectStatus('btn_pos', 'btn-pos')"
                        v-if="node.hasProperty('btn_pos')"
                        v-on:change="node.adjust('btn-pos')">
                        <option :value=item.value v-for="(item) in node.btn_pos_options" :key=item.description>
                            {{ item.description }}</option>
                    </form-select>

                    <form-input name="image url" type="text" :no_scroll=true v-model="node.src"
                        v-bind="objectStatus('src', 'reload-image')"
                        v-if="node.hasProperty('src')" v-on:input="node.adjust('reload-image')" />
                    <!-- Image selection itself now lives in the generic resource-property
                         Accordion loop below (ImageResourcePicker.vue, registered in
                         resourceUIRegistry.js) — this "image url" field remains as a
                         separate raw-text escape hatch, unrelated to resource selection. -->
                    <!-- <form-checkbox label="image local" v-if="node.hasProperty('src')" v-model="node.imageLocal"
                        v-on:change="node.setImageLocal($event)" /> -->
                </div>
                <div v-if="node.obj == 'page'">
                    <div class="flex mt-2 mb-1 relative w-full">
                        <form-label>page nav_limit</form-label>
                        <button class="absolute right-0" @click="node.addPageNav('next', 0)"><font-awesome-icon
                                icon="fa-solid fa-plus-circle" style="color: white;" /></button>
                    </div>
                    <div class="flex ml-3 pt-1 relative w-full" v-for="(val, index) in node.page_limit_commands"
                        :key="index">
                        <!-- <div class="flex pt-1 w-full"> -->
                        <form-select class="w-1/2" v-model="val.page_limit_command">
                            <option :value=name v-for="(name, nameIndex) in ['', 'prev', 'next', 'back']"
                                :key=nameIndex>{{
                                    name }} </option>
                        </form-select>
                        <form-select class=" w-1/3" v-model="val.page_limit_number">
                            <option :value=name v-for="(name, numberIndex) in node.getLayer().getPagenumbers()"
                                :key=numberIndex>
                                {{ name }} </option>
                        </form-select>
                        <button class="absolute right-3" @click="node.deletePageNav(index)"><font-awesome-icon
                                icon="fa-solid fa-trash-can" style="color: white;" /></button>
                        <!-- </div> -->
                    </div>

                    <div class="mt-3 pt-2 border-t border-slate-400 space-y-2">
                        <!-- Page transitions carry a real stored override
                             (undefined = inherit page 0 / firmware), so the
                             indicator's reset deletes it through the same
                             setter the select uses — the 'unset' option stays
                             as the explicit way to pick inheritance. -->
                        <form-select :name="pageTransitionLabel" :model-value="pageTransitionValue"
                            v-bind="unsetStatus(pageTransitionOverrides.transition, () => setPageTransition('unset'))"
                            @update:model-value="setPageTransition">
                            <option value="unset">{{ pageTransitionUnsetLabel }}</option>
                            <option value="none">None</option>
                            <option value="slide">Slide</option>
                        </form-select>

                        <div class="text-[10px] leading-none text-slate-300/80 pl-[41%]"
                            v-if="pageTransitionResolvedSummary">
                            Resolved: {{ pageTransitionResolvedSummary }}
                        </div>

                        <template v-if="pageTransitionValue !== 'none'">
                            <form-select name="Direction" :model-value="pageTransitionDirectionValue"
                                v-bind="unsetStatus(pageTransitionOverrides.direction, () => setPageTransitionDirection('unset'))"
                                @update:model-value="setPageTransitionDirection">
                                <option value="unset">{{ pageTransitionUnsetLabel }}</option>
                                <option value="auto">Auto</option>
                                <option value="left">Left</option>
                                <option value="right">Right</option>
                                <option value="up">Up</option>
                                <option value="down">Down</option>
                            </form-select>

                            <!-- The status icon is now the single way to fall back
                                 to the inherited duration; the old "use default"
                                 checkbox next to the field would be a second reset
                                 control for the same override. Empty field +
                                 placeholder still show that nothing is set. -->
                            <form-input :name="pageDurationLabel" type="number" :no_scroll=true min="0"
                                :model-value="pageTransitionDurationValue"
                                :placeholder="pageDurationPlaceholder"
                                v-bind="unsetStatus(pageTransitionOverrides.duration, () => setPageTransitionDurationUnset(true))"
                                @update:model-value="setPageTransitionDuration"
                            />
                            <span class="self-center text-xs text-gray-300">ms</span>
                        </template>
                    </div>
                </div>
                <property-row v-if="node.hasProperty('color')" label="color value"
                    v-bind="objectStatus('color', 'color')">
                    <ColorPicker :key="editorKey('object:color')" v-model:pureColor="node.color" useType="pure" format="hex" lang="En" shape="circle"
                        v-on:pureColorChange="node.adjust('color')" />
                </property-row>

                <property-row v-if="node.hasProperty('value_color')" label="value color"
                    v-bind="objectStatus('value_color', 'value_color')">
                    <color-picker v-model:pureColor="node.value_color" useType="pure" format="hex" lang="En"
                        shape="circle" v-on:pureColorChange="node.adjust('value_color')" />
                </property-row>
            </Accordion>

            <!-- One sibling accordion per group main's schema declares in its
                 optional `propertyGroups` metadata (see v2/shared.js), at the
                 same level as the main properties block above. Membership,
                 order and labels all come from the schema — this component only
                 renders what it is told, so a new group needs no change here.
                 The controls are the same PartPropertiesPanel instances as
                 before (border_side keeps its six-button editor); state
                 overrides stay in StatesPanel. -->
            <Accordion class="pl-2 pr-2 space-y-2 bg-opacity-30 bg-slate-400"
                v-for="group in propertyGroups" :key="group.id" :title="group.label"
                :modified="isMainGroupModified(group.props)">
                <!-- The padding group is the one group whose five properties are
                     not independent fields: Overall drives all four sides and
                     the sides normalize back into it, so it gets a dedicated
                     editor instead of the generic per-property controls. -->
                <PaddingEditor v-if="group.id === 'padding'" :key="editorKey('main:padding')"
                    :node="node" partName="main" />
                <PartPropertiesPanel v-else :key="editorKey(`main:${group.id}`)" :node="node" partName="main" :only="group.props" />
            </Accordion>

            <BadgePropertiesEditor v-if="node.obj === 'badge'" :node="node" />
            <ConnectionPropertiesEditor v-if="node.obj === 'connection'" :node="node" />

            <StatesPanel v-if="node.schemaV2" :node="node" />

            <Accordion class="pl-2 pr-2 space-y-2 bg-opacity-30 bg-slate-400"
                :title='node.obj?.toUpperCase() + " Indicator"' v-if="node.hasPart('indicator')"
                :modified="isPartGroupModified('indicator')">
                <PartPropertiesPanel :key="editorKey('indicator')" :node="node" partName="indicator" />
            </Accordion>

            <Accordion class="pl-2 pr-2 space-y-2 bg-opacity-30 bg-slate-400"
                :title='node.obj?.toUpperCase() + " Knob"' v-if="node.hasPart('knob')"
                :modified="isPartGroupModified('knob') || isObjectGroupModified(['adjustable'])">
                <!-- adjustable is a top-level arc property grouped here for convenience -->
                <form-checkbox label="adjustable" v-bind="objectStatus('adjustable', 'adjustable')"
                    v-if="node.hasProperty('adjustable')" v-model="node.adjustable"
                    v-on:change="node.adjust('adjustable', $event)" />
                <PartPropertiesPanel :key="editorKey('knob')" :node="node" partName="knob" />
            </Accordion>

            <!-- Remaining LVGL parts, schema-driven — 'main' is rendered above
                 inline with geometry, and 'indicator'/'knob' keep dedicated
                 blocks above since they render extra content. Accordion title
                 comes from the part's `label` metadata (see
                 v2/objects/dropdown.js for the reference implementation) with
                 a fallback for schemas not yet migrated to part metadata. -->
            <Accordion class="pl-2 pr-2 space-y-2 bg-opacity-30 bg-slate-400"
                v-for="partName in genericParts" :key="partName"
                :title="partLabel(partName)"
                :modified="isPartGroupModified(partName)">
                <PartPropertiesPanel :key="editorKey(partName)" :node="node" :partName="partName" />
            </Accordion>

            <Accordion class="pl-2 pr-2 space-y-2 bg-opacity-30 bg-slate-400"
                v-for="[propName, meta] in resourceProperties" :key="propName"
                :title="propLabel(propName, meta)"
                :modified="isObjectGroupModified([propName])">
                <component
                    v-if="resourceOverride(meta.resourceType)"
                    :is="resourceOverride(meta.resourceType)"
                    :node="node"
                    @open-dataset="$emit('open-dataset', $event)" />
                <ResourcePicker
                    v-else
                    :resource-type="meta.resourceType"
                    :model-value="node[propName]"
                    @update:model-value="onResourcePropertyChange(propName, $event)" />
            </Accordion>

            <Accordion class="pl-2 pr-2 space-y-2 bg-opacity-30 bg-slate-400"
                title="Grid" v-if="node.obj === 'chart'"
                :modified="isMainGroupModified(CHART_GRID_PROPS)">
                <PartPropertiesPanel :key="editorKey('chart-grid')" :node="node" partName="main"
                    :only="['grid_color', 'hdiv', 'vdiv']" />
            </Accordion>

            <Accordion class="pl-2 pr-2 space-y-2 bg-opacity-30 bg-slate-400"
                title="Layout" v-if="node.obj === 'legend'"
                :modified="isMainGroupModified(LEGEND_LAYOUT_PROPS)">
                <PartPropertiesPanel :key="editorKey('legend-layout')" :node="node" partName="main"
                    :only="['direction', 'columns', 'spacing', 'show_label', 'show_unit']" />
                <LegendValuesOrder :node="node" v-if="node.hasStyleProperty('main', 'values_order')" />
            </Accordion>

            <Accordion class="pl-2 pr-2 space-y-2 bg-opacity-30 bg-slate-400" title="Data"
                v-if="node.has_data"
                :modified="isObjectGroupModified(DATA_PROPS)">
                <form-input name="min value" type="number" :no_scroll=true step="0.1" v-model="node.min"
                    v-bind="objectStatus('min', 'data_min')"
                    v-if="node.hasProperty('min')" v-on:input="node.adjust('data_min')" />
                <form-input name="max value" type="number" :no_scroll=true step="0.1" v-model="node.max"
                    v-bind="objectStatus('max', 'data_max')"
                    v-if="node.hasProperty('max')" v-on:input="node.adjust('data_max')" />
                <form-input name="step" type="number" :no_scroll=true min="0" step="0.1" v-model="node.step"
                    v-bind="objectStatus('step', 'data_step')"
                    v-if="node.hasProperty('step')" v-on:input="node.adjust('data_step')"
                    @commit="commitDataProperty('step')" />
                <form-input name="start value" type="number" :no_scroll=true
                    :min="node.hasProperty('step') ? node.min : undefined"
                    :max="node.hasProperty('step') ? node.max : undefined"
                    :step="numberInputStep() ?? undefined" v-model="node.start_value"
                    v-bind="objectStatus('start_value', 'data_start_value')"
                    v-if="node.hasProperty('start_value') && isDataPropertyVisible('start_value')"
                    v-on:input="node.adjust('data_start_value')"
                    @commit="commitDataProperty('start_value')" />
                <form-checkbox label="auto range from data" v-bind="objectStatus('auto_range', 'auto_range')"
                    v-if="node.hasProperty('auto_range')" v-model="node.auto_range"
                    v-on:change="node.adjust('auto_range')" />
                <form-input name="scale factor" type="number" :no_scroll=true min="0.001" step="0.1" v-model="node.scale"
                    v-bind="objectStatus('scale', 'scale')"
                    v-if="node.hasProperty('scale')" v-on:input="node.adjust('scale')" />
                <form-input name="point count" type="number" :no_scroll=true min="1" v-model="node.point_count"
                    v-bind="objectStatus('point_count', 'point_count')"
                    v-if="node.hasProperty('point_count')" v-on:input="node.adjust('point_count')" />
                <form-input name="decimal places" type="number" :no_scroll=true min="0" max="4" v-model="node.decimals"
                    v-bind="objectStatus('decimals', 'decimals')"
                    v-if="node.hasProperty('decimals')" v-on:input="node.adjust('decimals')" />
                <div v-if="node.hasProperty('series') && !node.dataset" class="space-y-1 pt-1 border-t border-slate-400">
                    <form-label>Series colors</form-label>
                    <ChartSeriesColors :node="node" />
                </div>
                <form-input name="critical value" type="number" v-model="node.critical_value"
                    v-bind="objectStatus('critical_value', 'critical-value')"
                    v-if="node.critical_value !== undefined" v-on:input="node.adjust('critical-value')" />
                <form-input name="current value" type="number"
                    :min="node.hasProperty('step') ? node.min : undefined"
                    :max="node.hasProperty('step') ? node.max : undefined"
                    :step="numberInputStep() ?? undefined" v-model="node.val" v-bind="objectStatus('val', 'data_val')"
                    v-if="node.hasProperty('val') && !(node.obj === 'gauge' && node.dataset)"
                    v-on:input="node.adjust('data_val')"
                    @commit="commitDataProperty('val')" />
                <form-input name="value" type="number" v-model="node.value" v-bind="objectStatus('value', 'value')"
                    v-if="node.hasProperty('value')"
                    v-on:input="node.adjust('value')" />
                <form-input name="format" type="number" :no_scroll=true min="0" max="4" v-model="node.format"
                    v-bind="objectStatus('format', 'data-format')"
                    v-if="node.hasProperty('format')" v-on:input="node.adjust('data-format', $event.target.value)" />
            </Accordion>

            <GaugeNeedlesEditor v-if="node.obj === 'gauge'" :node="node" />

            <Accordion class="pr-2 pl-2 space-y-2 bg-opacity-30 bg-slate-400" title="Home Assistant"
                v-if="node.obj !== 'template_page' && (node.hass_properties?.length > 0 || node.hass_services?.length > 0)">
                <HassObjectSummary :node="node" />
            </Accordion>

            <Accordion class="pr-2 pl-2 space-y-2 bg-opacity-30 bg-slate-400" title="GPIO State Bindings"
                :modified="Boolean(node.gpio_bindings?.state?.length)"
                v-if="node.obj !== 'page' && node.obj !== 'template_page'">
                <LocalGpioEditor :node="node" :settings="settings" state-only embedded />
                <LogicGpioBindingsSummary :node="node" />
            </Accordion>

            <Accordion class="pr-2 pl-2 space-y-2 bg-opacity-30 bg-slate-400" title="Actions"
                v-if="node.hass_events?.length > 0 || node.actions?.length > 0">
                <LogicObjectSummary :node="node" />
                <div class="space-y-1.5 mb-1">
                    <!-- Group-id has no schema default (no group is the empty
                         option itself), so the status column stays reserved but
                         empty — the row keeps the panel's alignment. -->
                    <property-row v-if="node.has_group" :resettable="false">
                        <template #label>
                            <div class="flex items-center gap-1 min-w-0">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="w-3 h-3 text-slate-300/70 shrink-0">
                                    <path d="M3.5 2A1.5 1.5 0 002 3.5v9A1.5 1.5 0 003.5 14h9a1.5 1.5 0 001.5-1.5v-9A1.5 1.5 0 0012.5 2h-9zm5.25 3.75a.75.75 0 00-1.5 0V7H5.75a.75.75 0 000 1.5H7.25v1.25a.75.75 0 001.5 0V8.5h1.5a.75.75 0 000-1.5h-1.5V5.75z"/>
                                </svg>
                                <span class="text-xs text-white/70">Group-id</span>
                            </div>
                        </template>
                        <select
                            class="w-full min-w-0 px-1 border border-gray-400/60 hover:border-gray-400 focus:border-blue-500 bg-slate-800/60 text-white text-xs rounded focus:outline-none appearance-none"
                            style="height: 24px;"
                            v-model="node.groupid"
                            v-on:change="node.getLayer().updateObjectMenu(node)"
                        >
                            <option class="bg-slate-800" value="" key="gid_none" />
                            <option class="bg-slate-800" :value="group_id" v-for="(group_id) in groups" :key="group_id">group {{ group_id }}</option>
                        </select>
                    </property-row>
                </div>
            </Accordion>
        </div>
    </div>
    <IconPicker v-model="showIconPicker" :initialText="node?.text || ''" :availableTabs="['icons', 'variables']" @text-updated="onTextUpdated" />
    <IconPicker v-model="showTemplatePicker" :initialText="node?.template || ''" :availableTabs="['datetime']" :defaultTab="'datetime'" @text-updated="onTemplateUpdated" />
    <IconPicker v-model="showValueStrPicker" :initialText="node?.value_str || ''" :availableTabs="['icons', 'variables']" @text-updated="onValueStrUpdated" />
    <ObjectConversionDialog v-if="showConversionDialog && conversionSource"
        :visible="showConversionDialog"
        :current-label="getObjectTypeDisplayName(node?.obj)"
        :source-snapshot="conversionSource"
        :targets="conversionTargets"
        @close="closeConversionDialog"
        @confirm="confirmConversion" />
</template>

<script>
import { node } from 'prop-types';
import { ColorPicker } from "vue3-colorpicker";
import "vue3-colorpicker/style.css";
import Accordion from '../Accordion.vue';
import HassObjectSummary from '../hass/HassObjectSummary.vue';
import LocalGpioEditor from '../gpio/LocalGpioEditor.vue';
import LogicGpioBindingsSummary from '../logic/LogicGpioBindingsSummary.vue';
import ListOptionsTable from '../ListOptionsTable.vue';
import BadgePropertiesEditor from './BadgePropertiesEditor.vue';
import GaugeNeedlesEditor from './GaugeNeedlesEditor.vue';
import ConnectionPropertiesEditor from './ConnectionPropertiesEditor.vue';
import IconPicker from '../IconPicker.vue';;
// The two directions of the icons-in-text display convention this panel
// established, now shared with the binding editors' String Builder field.
import { iconCharsToNotation, iconNotationToChars } from '../../utils/iconTextNotation.js';
import PartPropertiesPanel from './PartPropertiesPanel.vue';
import PaddingEditor from './PaddingEditor.vue';
import PropertyRow from './PropertyRow.vue';
import StatesPanel from './StatesPanel.vue';
import ChartSeriesColors from './ChartSeriesColors.vue';
import LegendValuesOrder from './LegendValuesOrder.vue';
import LogicObjectSummary from '../logic/LogicObjectSummary.vue';
import ObjectConversionDialog from './ObjectConversionDialog.vue';
import ResourcePicker from '../resources/ResourcePicker.vue';
import { getPropertyEditorComponent } from '../resources/resourceUIRegistry.js';
import { partLabel as getPartLabel } from './partLabels.js';
import {
    isObjectPropertyResettable,
    isObjectPropertyModified,
    resetObjectProperty,
} from '../../Hasp/properties/objectPropertyStatus.js';
import {
    isPartModified,
    areObjectPropertiesModified,
    areTopLevelPropertiesModified,
    isGroupModified,
} from '../../Hasp/properties/propertyGroupStatus.js';
import { styleRefreshMixin } from './styleRefreshMixin.js';
import { propertyEditorKey } from './propertyEditorKey.js';
import {
    captureConversionSource,
    convertObjectType,
    getObjectConversionTargets,
    getObjectTypeDisplayName,
} from '../../Hasp/properties/v2/objectConversion.js';
import {
    isValidStretchMode,
    stretchModeLabel as getStretchModeLabel,
    stretchModeOptions as getStretchModeOptions,
} from '../../Hasp/properties/v2/stretchMode.js';

// The two curated accordions whose membership is not schema group metadata, and
// the Data accordion's top-level properties — the same lists their controls are
// rendered from, so the indicator can never cover a different set than the
// group shows.
const CHART_GRID_PROPS = ['grid_color', 'hdiv', 'vdiv'];
const LEGEND_LAYOUT_PROPS = ['direction', 'columns', 'spacing', 'show_label', 'show_unit', 'values_order'];
const DATA_PROPS = ['min', 'max', 'step', 'auto_range', 'scale', 'point_count', 'decimals', 'critical_value', 'start_value', 'val', 'value', 'format'];

export default {
    // Style writes bypass Vue reactivity; the mixin re-renders this panel on the
    // node's own style-refresh events so every accordion indicator (and the
    // per-property one) updates the moment a value changes or is reset.
    mixins: [styleRefreshMixin],
    components: {
        Accordion,
        ColorPicker,
        HassObjectSummary,
        LocalGpioEditor,
        LogicGpioBindingsSummary,
        ListOptionsTable,
        BadgePropertiesEditor,
        GaugeNeedlesEditor,
        ConnectionPropertiesEditor,
        IconPicker,
        PartPropertiesPanel,
        PaddingEditor,
        PropertyRow,
        StatesPanel,
        ChartSeriesColors,
        LegendValuesOrder,
        LogicObjectSummary,
        ObjectConversionDialog,
        ResourcePicker,
    },
    props: {
        node: {
            type: Object,
            required: true,
        },
        settings: {
            type: Object,
            required: true,
        },
        updateNode: {
            type: Number,
            default: 0,
        },
    },
    // Declared so these listeners aren't treated as failed attribute
    // fall-through: this panel renders a fragment (multiple root nodes), so
    // undeclared listeners can't be auto-inherited and Vue warns about them.
    // 'update:node' backs the parent's v-model:node; 'open-dataset' bubbles up
    // from the dataset property editor (see template).
    emits: ['open-dataset', 'update:node'],
    data: () => {
        return {
            CHART_GRID_PROPS,
            LEGEND_LAYOUT_PROPS,
            DATA_PROPS,
            geom_x: 0,
            geom_y: 0,
            geom_w: 0,
            geom_h: 0,
            maxLinewidth: "40",
            // Bumped after a reset so the status bindings re-evaluate — node is
            // a Konva instance and not reactive for every field.
            statusTick: 0,
            // Bumped when a schema-declared visibility dependency changes.
            propertyVisibilityTick: 0,
            showIconPicker: false,
            showTemplatePicker: false,
            showValueStrPicker: false,
            showConversionDialog: false,
            conversionSource: null,
        };
    },
    mounted() {
        this.subscribePropertyVisibility();
    },
    beforeUnmount() {
        this.unsubscribePropertyVisibility();
    },
    methods: {
        setHaspId(value) {
            this.node?.getLayer?.()?.setObjectHaspId?.(this.node, value);
            this.statusTick++;
        },
        editorKey(context) {
            return propertyEditorKey(this.node, context);
        },
        subscribePropertyVisibility() {
            this.unsubscribePropertyVisibility();
            this._propertyVisibilitySubs = [];
            if (!this.node?.on) return;
            for (const property of this.node.getPropertyVisibilityDependencies?.() ?? []) {
                const handler = () => { this.propertyVisibilityTick++; };
                this.node.on(property, handler);
                this._propertyVisibilitySubs.push({ property, handler });
            }
            const trackImageHandler = () => {
                this.statusTick++;
                this.syncGeom();
            };
            this.node.on('track-image-size', trackImageHandler);
            this._propertyVisibilitySubs.push({ property: 'track-image-size', handler: trackImageHandler });
        },
        unsubscribePropertyVisibility() {
            for (const { property, handler } of this._propertyVisibilitySubs ?? []) {
                this.node?.off?.(property, handler);
            }
            this._propertyVisibilitySubs = [];
        },
        isDataPropertyVisible(property) {
            void this.propertyVisibilityTick;
            return this.node?.isPropertyVisible?.(property) ?? true;
        },
        isTrackImageSizeLocked() {
            void this.statusTick;
            return this.node?._trackImageSizeLocked === true;
        },
        geometrySizeTitle() {
            return this.isTrackImageSizeLocked()
                ? 'Locked to the selected native-size track image'
                : 'Calculated automatically — see Size Mode';
        },
        // ------------------------------------------------------------------
        // Override status for top-level (non-style) properties.
        //
        // One binding per row — v-bind="objectStatus('angle', 'angle')" — hands
        // PropertyRow/form-input/form-select/form-checkbox the three status
        // props plus the reset handler, so no row builds icons or tooltips of
        // its own. All three answers come from objectPropertyStatus.js, which
        // owns the "still on its effective default" rule shared with the
        // exporter; nothing here knows any default value.
        //
        // `adjustKey` is the same node.adjust() key the row's own editor uses,
        // so a reset notifies the node through exactly the edit path a manual
        // change would. `apply` overrides that for the handful of properties
        // whose editor writes through a dedicated method instead.
        // ------------------------------------------------------------------
        // ---- Accordion "contains modified properties" indicators ----------
        // Informational only (no reset at group level). Every answer comes from
        // propertyGroupStatus.js, which asks node.hasExplicitStyle() /
        // objectPropertyStatus.js — the exact same truth the per-property icons
        // use. styleTick/statusTick are read to register render dependencies.
        isMainGroupModified(props) {
            void this.styleTick;
            return isPartModified(this.node, 'main', { only: props });
        },

        isPartGroupModified(partName) {
            void this.styleTick;
            return isPartModified(this.node, partName);
        },

        isObjectGroupModified(props) {
            void this.statusTick;
            return areObjectPropertiesModified(this.node, props);
        },

        objectStatus(prop, adjustKey = null, apply = null) {
            // statusTick is read purely to re-render after a reset: node is a
            // Konva instance, not guaranteed to be reactive for every field.
            void this.statusTick;
            return {
                resettable: isObjectPropertyResettable(this.node, prop),
                modified: isObjectPropertyModified(this.node, prop),
                onReset: () => this.resetObjectProp(prop, adjustKey, apply),
            };
        },

        numberInputStep() {
            const step = Number(this.node?.step);
            return Number.isFinite(step) && step > 0 ? step : undefined;
        },

        commitDataProperty(property) {
            this.node?.commitDataProperty?.(property);
        },

        resetObjectProp(prop, adjustKey = null, apply = null) {
            if (!this.node) return;
            const value = resetObjectProperty(this.node, prop);
            if (value === undefined && !isObjectPropertyResettable(this.node, prop)) return;
            if (apply) apply(value);
            else if (adjustKey) this.node.adjust(adjustKey, value);
            this.statusTick++;
            this.syncGeom();
        },

        // Same three props for a property whose "explicit" state is not a
        // default comparison but a stored override the node can delete itself
        // (page transitions: undefined = inherit page 0 / firmware).
        unsetStatus(isSet, unset) {
            void this.statusTick;
            return {
                resettable: true,
                modified: isSet,
                inherited: true,
                onReset: () => { unset(); this.statusTick++; },
            };
        },

        // Status for a main-part STYLE property edited from this panel rather
        // than from PartPropertiesPanel (text_decor) — explicit-vs-inherited
        // stays node.hasExplicitStyle(), reset stays node.resetStateStyle().
        mainStyleStatus(prop) {
            void this.statusTick;
            return {
                resettable: true,
                modified: this.node?.hasExplicitStyle('main', prop, 'default') ?? false,
                onReset: () => {
                    this.node?.resetStateStyle('main', 'default', prop);
                    this.statusTick++;
                },
            };
        },

        // Derive a resource property's Accordion title from its schema
        // `label`, falling back to the property name (same convention as
        // PartPropertiesPanel's propLabel).
        propLabel(propName, meta) {
            return meta?.label ?? propName.replace(/_/g, ' ');
        },
        stretchModeOptions() {
            return getStretchModeOptions(this.node);
        },
        stretchModeLabel(mode) {
            return getStretchModeLabel(mode);
        },
        setStretchMode(mode) {
            if (!this.node || !isValidStretchMode(this.node, mode)) return;
            if (this.node.stretch_mode === mode) return;
            this.node.stretch_mode = mode;
            this.node.adjust?.('stretch_mode', mode);
            this.node.getLayer?.()?.updateTransformer?.();
            this.statusTick++;
        },
        // Widget-specific component to use for a resourceType's editor
        // instead of the bare ResourcePicker, if one is registered.
        resourceOverride(resourceType) {
            return getPropertyEditorComponent(resourceType);
        },
        // Accordion title for a generic part — schema `label` metadata wins,
        // otherwise fall back to the title used before parts carried metadata.
        // See partLabels.js — shared with StatesPanel.vue.
        partLabel(partName) {
            return getPartLabel(this.node, partName);
        },
        // Generic handler for resource properties with no widget-specific
        // override component — plain assign-or-clear plus the standard
        // node.adjust() change notification used throughout this panel.
        onResourcePropertyChange(propName, value) {
            if (value) {
                this.node[propName] = value;
            } else {
                delete this.node[propName];
            }
            this.node.adjust(propName);
        },
        setTargetPage(value) {
            if (!this.node) return;
            this.node.targetPage = value === 'null' || value === '' ? null : Number(value);
            this.node.getLayer().updateTemplateUnderlay();
        },
        setPageTransition(value) {
            if (!this.node) return;
            this.node.transition = value === 'unset' ? undefined : value;
            this.node.adjust('transition');
        },
        setPageTransitionDirection(value) {
            if (!this.node) return;
            this.node.transition_direction = value === 'unset' ? undefined : value;
            this.node.adjust('transition_direction');
        },
        setPageTransitionDuration(value) {
            if (!this.node) return;
            if (value === '' || value === null || value === undefined || Number.isNaN(Number(value))) {
                this.node.transition_duration = undefined;
            } else {
                this.node.transition_duration = Number(value);
            }
            this.node.adjust('transition_duration');
        },
        setPageTransitionDurationUnset(disabled) {
            if (!this.node) return;
            if (disabled) {
                delete this.node.transition_duration;
                this.node.adjust('transition_duration');
                return;
            }
        },
        syncGeom() {
            if (!this.node) return;
            this.geom_x = Math.round(this.node.x());
            this.geom_y = Math.round(this.node.y());
            this.geom_w = Math.round(this.node.width());
            this.geom_h = Math.round(this.node.height());
        },
        applyGeom(field) {
            if (!this.node) return;
            if (['w', 'h', 'size'].includes(field) && this.isTrackImageSizeLocked()) {
                this.syncGeom();
                return;
            }
            // Capture pre-change geometry for history command
            const layer = this.node.getLayer?.();
            if (layer) {
                layer._preGeomState = {
                    x: this.node.x(), y: this.node.y(),
                    w: this.node.width(), h: this.node.height(),
                };
            }
            if (field === 'x') {
                this.node.x(this.geom_x);
            } else if (field === 'y') {
                this.node.y(this.geom_y);
            } else if (field === 'w') {
                const minW = this.node.min_width || 1;
                const w = Math.max(minW, this.geom_w);
                this.geom_w = w;
                this.node.setNewSize({ width: w, height: this.node.height() });
            } else if (field === 'h') {
                const minH = this.node.min_height || 1;
                const h = Math.max(minH, this.geom_h);
                this.geom_h = h;
                this.node.setNewSize({ width: this.node.width(), height: h });
            } else if (field === 'size') {
                const min = this.node.min_width || 1;
                const s = Math.max(min, this.geom_w);
                this.geom_w = s;
                this.geom_h = s;
                this.node.setNewSize({ width: s, height: s });
            }
            this.node.transformEnd();
            this.node.getLayer().transformer.forceUpdate();
            this.node.getLayer().batchDraw();
            this.node.getLayer().layerCallback('geom-changed', this.node);
        },
        onSizeModeChange() {
            if (!this.node) return;
            // Entering (or staying in) 'auto' recomputes size from content;
            // updateTransformer() re-reads size_kind to hide/show resize handles.
            this.node.drawLegend?.();
            this.node.getLayer().updateTransformer();
            this.node.getLayer().batchDraw();
            this.syncGeom();
        },
        // text_font_family is a part-scoped style property (main) on some
        // widgets (label, btn, btnmatrix, checkbox) but a top-level widget
        // property on others (tabview). Route through setStyle when it's
        // part-scoped -- it fires both the legacy wire-key event AND the new
        // style-refresh event, so it works whether or not the widget's class
        // has been migrated to onStyle() yet (setStyle also no-ops
        // internally if the value is unchanged). Fall back to the legacy
        // adjust() path only when it's genuinely not a style.
        setTextFontFamily(value) {
            const isPartStyle = this.node.hasStyleProperty('main', 'text_font_family');
            if (isPartStyle) {
                this.node.setStyle('main', 'text_font_family', value);
            } else if (this.node.text_font_family !== value) {
                this.node.text_font_family = value;
                this.node.adjust('text_font_family');
            }
        },
        onTextUpdated(newText) {
            this.node.text = newText;
            if (newText && /[\uE000-\uF8FF]/.test(newText)) {
                this.setTextFontFamily('Material Icons');
            }
            this.node.adjust('text_text');
        },
        onTemplateUpdated(newTemplate) {
            this.node.template = newTemplate;
            this.node.adjust('template_text');
        },
        onValueStrUpdated(newText) {
            this.node.value_str = newText;
            this.node.adjust('value_str');
        },
        getObjectTypeDisplayName,
        openConversionDialog() {
            if (!this.node || this.conversionTargets.length === 0) return;
            this.conversionSource = captureConversionSource(this.node);
            this.showConversionDialog = true;
        },
        closeConversionDialog() {
            this.showConversionDialog = false;
            this.conversionSource = null;
        },
        confirmConversion(targetType) {
            if (!this.node) return;
            try {
                convertObjectType(this.node, targetType);
                this.closeConversionDialog();
            } catch (error) {
                console.error('Object conversion failed:', error);
                window.alert(error?.message || 'Object conversion failed.');
            }
        },
    },
    watch: {
        node: {
            immediate: true,
            handler: function () {
                this.unsubscribePropertyVisibility();
                this.subscribePropertyVisibility();
                this.syncGeom();
            },
        },
        updateNode() {
            this.syncGeom();
        },
    },
    computed: {
        conversionTargets() {
            return getObjectConversionTargets(this.node?.obj);
        },
        runtimeIdError() {
            void this.statusTick;
            return this.node?._runtimeIdValidationError || '';
        },
        suggestedHaspId() {
            void this.statusTick;
            return this.node?.getLayer?.()?.getSuggestedObjectHaspId?.(this.node) ?? null;
        },
        groups() {
            return Array.from({ length: 12 }, (_, index) => index + 1);
        },

        // The accordions to render beside the general one, straight from main's
        // optional `propertyGroups` schema metadata (v2/shared.js). A group's
        // declared property list is intersected with what this part actually
        // declares, which is what lets every widget share one group list: page
        // declares borders but no outline/shadow, so those groups resolve empty
        // and are dropped here rather than rendering an empty accordion.
        // Order inside a group is the group's own declared order.
        //
        // Internal properties (outline_opa, carried as outline_color's alpha)
        // are skipped: PartPropertiesPanel never renders them, so a group
        // holding only internal properties would show an empty accordion.
        propertyGroups() {
            const partDef = this.node?.getStyleDefinition?.('main');
            const properties = partDef?.properties ?? {};
            return (partDef?.propertyGroups ?? [])
                .map(group => ({
                    ...group,
                    props: (group.properties ?? []).filter(
                        name => properties[name] && !properties[name].internal),
                }))
                .filter(group => group.props.length > 0);
        },

        // What main's general property list must NOT render: the curated
        // per-widget lists that already moved to their own accordions, plus
        // every property shown in a schema-declared group accordion above.
        mainExclude() {
            const curated = this.node?.obj === 'chart'
                ? ['grid_color', 'hdiv', 'vdiv', 'y_scale', 'x_scale', 'scale_text_color', 'scale_font_size', 'scale_tick_color']
                    : this.node?.obj === 'legend'
                        ? ['direction', 'columns', 'spacing', 'show_label', 'show_unit', 'values_order', 'marker_size', 'marker_shape', 'text_font', 'label_color']
                        : this.node?.obj === 'badge'
                            ? ['text_font']
                            : [];
            return [...curated, ...this.propertyGroups.flatMap(group => group.props)];
        },
        // Parts rendered generically as a plain Accordion + PartPropertiesPanel,
        // sorted by the schema's `order` metadata (see v2/shared.js part
        // metadata doc). Parts that omit `order` keep their declaration
        // position — schemas that don't use `order` at all render unchanged.
        // 'main' is merged into the top-level properties block above; 'indicator'
        // and 'knob' keep dedicated blocks above because they render extra
        // content (dynamic title prefix, the arc-only 'adjustable' checkbox) —
        // their position is not schema-driven, a known limitation left as-is
        // per this change's infrastructure-only scope.
        genericParts() {
            const partNames = this.node?.getPartNames() ?? [];
            return partNames
                .filter(p => !['main', 'indicator', 'knob'].includes(p))
                .map((name, index) => ({ name, order: this.node.getStyleDefinition(name)?.order ?? index }))
                .sort((a, b) => a.order - b.order)
                .map(p => p.name);
        },
        // Every top-level schema property declared `type: 'resource'` for the
        // selected node — drives the generic resource-property Accordion loop
        // above so a new resource type never requires template changes here.
        // `meta.inline` properties (e.g. label.js's `font`) opt out — they're
        // rendered directly in the main properties list instead (see
        // inlineResourceProperties below).
        resourceProperties() {
            return this.node?.getResourceProperties() ?? [];
        },
        // The `meta.inline: true` counterpart to resourceProperties above —
        // drives the generic inline resource-property loop in the main
        // properties list (e.g. label.js's `font`, gauge.js's
        // `value_font_resource`, msgbox.js's `item_font_resource`) so a new
        // inline resource property never requires template changes here.
        inlineResourceProperties() {
            const properties = this.node?.getResourceProperties({ inline: true }) ?? [];
            if (this.node?.obj !== 'badge') return properties;
            return properties.filter(([propName]) =>
                !['text_font_resource', 'value_font_resource'].includes(propName));
        },
        // Top-level properties this widget renders in ANOTHER accordion, so the
        // main one never lights up for a change the user has to look elsewhere
        // for: the Data block, the arc-only `adjustable` grouped under Knob, and
        // every non-inline resource property (each has its own accordion).
        objectPropsShownElsewhere() {
            return [
                ...DATA_PROPS,
                'adjustable',
                ...this.resourceProperties.map(([propName]) => propName),
            ];
        },

        // A page transition property is overridden when it is stored at all
        // (undefined = inherit page 0 / firmware). One definition, used by the
        // rows' reset indicators and by the OBJ Properties group status.
        pageTransitionOverrides() {
            void this.statusTick;
            return {
                transition: this.node?.transition !== undefined,
                direction: this.node?.transition_direction !== undefined,
                duration: this.node?.transition_duration !== undefined,
            };
        },

        // The main accordion holds three kinds of controls: this widget's
        // top-level properties, main's own style properties (minus the ones
        // moved to their own accordions) and — for pages — the transition
        // overrides. Folded as one group tree so a further split later stays a
        // data change (see propertyGroupStatus.js).
        objectPropertiesModified() {
            void this.styleTick;
            void this.statusTick;
            return isGroupModified({
                id: 'object-properties',
                children: [
                    { id: 'top-level', modified: areTopLevelPropertiesModified(this.node, { exclude: this.objectPropsShownElsewhere }) },
                    { id: 'main-style', modified: isPartModified(this.node, 'main', { exclude: this.mainExclude }) },
                    { id: 'page-transition', modified: Object.values(this.pageTransitionOverrides).some(Boolean) },
                ],
            });
        },

        pageTransitionValue() {
            if (this.node?.transition === undefined) return 'unset';
            return this.node.transition;
        },
        pageTransitionLabel() {
            return this.node?.haspid === 0 ? 'Page Transition Defaults' : 'Page Transition';
        },
        pageTransitionUnsetLabel() {
            return this.node?.haspid === 0 ? 'Firmware default' : 'Use Page 0 default';
        },
        pageDurationLabel() {
            return this.node?.haspid === 0 ? 'Duration' : 'Duration';
        },
        pageDurationPlaceholder() {
            return this.node?.haspid === 0 ? 'Firmware default (250 ms)' : 'Use Page 0 default';
        },
        pageTransitionDirectionValue() {
            if (this.node?.transition_direction === undefined) return 'unset';
            return this.node.transition_direction;
        },
        pageTransitionDurationValue() {
            return this.node?.transition_duration;
        },
        pageTransitionResolvedSummary() {
            const page0 = this.node?.haspid === 0 ? this.node : this.node?.getLayer?.()?.findPage?.(0);
            const transition = this.node?.transition ?? page0?.transition ?? 'none';
            const direction = this.node?.transition_direction ?? page0?.transition_direction ?? 'auto';
            const duration = this.node?.transition_duration ?? page0?.transition_duration ?? 250;
            if (transition === 'none') return 'None';
            return `${transition === 'slide' ? 'Slide' : transition} · ${direction} · ${duration} ms`;
        },
        regularPages() {
            if (!this.node?.getLayer) return [];
            const layer = this.node.getLayer();
            if (!layer?.getChildren) return [];
            return layer.getChildren()
                .filter(c => c.obj === 'page' && c.haspid >= 0)
                .sort((a, b) => a.haspid - b.haspid);
        },
        displayValueStr: {
            get() {
                return iconCharsToNotation(this.node?.value_str);
            },
            set(value) {
                if (!this.node) return;
                this.node.value_str = iconNotationToChars(value);
            }
        },
        displayText: {
            get() {
                return iconCharsToNotation(this.node?.text);
            },
            set(value) {
                if (!this.node) return;
                this.node.text = iconNotationToChars(value);
            }
        }
    },


    // },
    // emits: ['updateNode'],
    // mounted() {
    //     window.addEventListener("keyup", this.dismissModal);
    // },
    // beforeDestroy() {
    //     window.removeEventListener("keyup", this.dismissModal);
    // },
    // methods: {
    //     updateNode: function (e) {
    //         this.node.redraw();
    //     }
    // }
}
</script>
